import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import relatedItemsApi from 'common/src/modules/relatedItems/relatedItems.api';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const setFeedToolSchema = z.object({
  feedId: z.string().describe(describeIdFormat(IdPrefix.ProductFeed)),
});

export default function setFeedTool() {
  async function execute({
    input: {feedId},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<typeof setFeedToolSchema, ProductCardScratchpadState>): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    const parseResult = idHelper.parseProductFeedId(feedId);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const numericFeedId = parseResult.numericId;

    if (!validatedScratchpad.card.availableFeeds?.length) {
      return toolError(
        'No feeds available for this card. First set the strategy, feeds are available when the strategy is set.',
      );
    }

    if (!validatedScratchpad.card.availableFeeds?.some((f) => f.id === numericFeedId)) {
      return toolError(
        `Feed ${idHelper.toProductFeedId(numericFeedId)} is not available for this card. Available feeds: ${validatedScratchpad.card.availableFeeds?.map((f) => `${idHelper.toProductFeedId(f.id)}: ${f.name}`).join(', ')}`,
      );
    }

    validatedScratchpad.card.feedId = numericFeedId;
    validatedScratchpad.card.feed = validatedScratchpad.card.availableFeeds?.find(
      (f) => f.id === numericFeedId,
    );
    validatedScratchpad.card.topItemLogicId = 0;
    validatedScratchpad.card.categoryTopItemsLogicId = 0;
    validatedScratchpad.card.retargetingProductSetId = 0;
    validatedScratchpad.card.productIds = [];

    const relatedItems = await relatedItemsApi.getRelatedItemsLogicsForFeed({
      feedId: numericFeedId,
    });
    validatedScratchpad.card.relatedItemsLogics = relatedItems;

    return toolResult(
      `Feed set to: ${idHelper.toProductFeedId(numericFeedId)}, you can continue setting the strategy related items`,
    );
  }

  return tool({
    name: V2CardAgentToolName.SetFeed,
    displayName: 'Set feed',
    description: 'Set the feed for the product card',
    execute,
    inputSchema: setFeedToolSchema,
  });
}
